import React, { useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import Select from "react-select";
import Modal from "react-modal";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "./CalendarPage.css";

import { enGB } from "date-fns/locale";

Modal.setAppElement("#root");

const locales = { "en-GB": enGB };

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

// IMPORTANT:
// Your database column is timestamp without time zone.
// So we save the local/SAST datetime string directly,
// instead of using toISOString(), which converts SAST to UTC.
function toLocalDateTimeString(date) {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}

// Handles dates returned from PostgreSQL/API.
// If a trailing Z appears, remove it so the browser does not shift the time.
function parseDbDate(value) {
    if (!value) return null;
    if (typeof value === "string") {
        return new Date(value.replace("Z", ""));
    }
    return new Date(value);
}

function CalendarPage({ user, openLoginModal }) {
    const [lodges, setLodges] = useState([]);
    const [constitutions, setConstitutions] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [events, setEvents] = useState([]);

    const [selectedLodge, setSelectedLodge] = useState(null);
    const [selectedConstitution, setSelectedConstitution] = useState(null);
    const [selectedEventType, setSelectedEventType] = useState(null);
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [startDateTime, setStartDateTime] = useState("");
    const [endDateTime, setEndDateTime] = useState("");
    const [description, setDescription] = useState("");
    const [isRecurring, setIsRecurring] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [editLodge, setEditLodge] = useState(null);
    const [editConstitution, setEditConstitution] = useState(null);
    const [editEventType, setEditEventType] = useState(null);
    const [editBuilding, setEditBuilding] = useState(null);
    const [editStartDateTime, setEditStartDateTime] = useState("");
    const [editEndDateTime, setEditEndDateTime] = useState("");
    const [editDescription, setEditDescription] = useState("");

    const [selectedLodgeFilters, setSelectedLodgeFilters] = useState([]);
    const [selectedEventTypeFilters, setSelectedEventTypeFilters] = useState([]);

    const reloadEvents = () => {
        fetch("/api/v1/events")
            .then((res) => res.json())
            .then((data) => {
                console.log("EVENTS LOADED:", data);
                if (Array.isArray(data)) setEvents(data);
                else {
                    console.error("Error fetching events:", data.error || data);
                    setEvents([]);
                }
            })
            .catch((err) => console.error("Error fetching events:", err));
    };

    useEffect(() => {
        fetch("/api/v1/lodges")
            .then((res) => res.json())
            .then(setLodges)
            .catch((err) => console.error("Error fetching lodges:", err));

        fetch("/api/v1/constitutions")
            .then((res) => res.json())
            .then(setConstitutions)
            .catch((err) => console.error("Error fetching constitutions:", err));

        fetch("/api/v1/eventtypes")
            .then((res) => res.json())
            .then(setEventTypes)
            .catch((err) => console.error("Error fetching event types:", err));

        fetch("/api/v1/buildings")
            .then((res) => res.json())
            .then(setBuildings)
            .catch((err) => console.error("Error fetching buildings:", err));

        reloadEvents();
    }, []);

    const transformedEvents = events.map((ev) => {
        const start = parseDbDate(ev.event_start);
        const end = parseDbDate(ev.event_end);

        const lodge = ev.lodge_name || "Unknown Lodge";
        const type = ev.event_type || "Event";
        const building = ev.building_name ? ` @ ${ev.building_name}` : "";
        const descriptionText = ev.description ? ` - ${ev.description}` : "";

        return {
            ...ev,
            start,
            end,
            title: `${lodge} | ${type}${building}`,
            tooltip: `${lodge}\n${type}${building}${descriptionText}`,
        };
    });

    const lodgeOptions = lodges.map((l) => ({ label: l.name, value: l.id }));
    const buildingOptions = buildings.map((b) => ({ label: b.name, value: b.id }));

    const usedEventTypeIDs = Array.from(new Set(events.map((ev) => ev.event_type_id)));
    const usedEventTypes = eventTypes.filter((et) => usedEventTypeIDs.includes(et.id));
    const eventTypeFilterOptions = usedEventTypes.map((et) => ({
        value: et.id,
        label: et.description,
    }));

    const filteredEvents = transformedEvents.filter((ev) => {
        if (selectedLodgeFilters.length > 0) {
            const lodgeIDs = selectedLodgeFilters.map((o) => o.value);
            if (!lodgeIDs.includes(ev.lodge_id)) return false;
        }

        if (selectedEventTypeFilters.length > 0) {
            const typeIDs = selectedEventTypeFilters.map((o) => o.value);
            if (!typeIDs.includes(ev.event_type_id)) return false;
        }

        return true;
    });

    const now = new Date();
    const upcomingEvents = filteredEvents
        .filter((event) => event.start && event.start >= now)
        .sort((a, b) => a.start - b.start)
        .slice(0, 6);

    const nextEvent = upcomingEvents[0] || null;
    const activeFilterCount = selectedLodgeFilters.length + selectedEventTypeFilters.length;
    const uniqueLodgeCount = new Set(filteredEvents.map((event) => event.lodge_id).filter(Boolean)).size;
    const uniqueVenueCount = new Set(filteredEvents.map((event) => event.building_id).filter(Boolean)).size;
    const recurringPreviewText = isRecurring ? "Creates 12 monthly events" : "Single event";

    const eventTypeCounts = Array.from(
        filteredEvents.reduce((map, event) => {
            const key = event.event_type || "Event";
            map.set(key, (map.get(key) || 0) + 1);
            return map;
        }, new Map())
    )
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);

    function findNthWeekday(dateObj) {
        const dayOfWeek = dateObj.getDay();
        const month = dateObj.getMonth();
        const year = dateObj.getFullYear();
        const dateNum = dateObj.getDate();

        let nth = 0;
        const tempDate = new Date(year, month, 1);

        while (tempDate.getMonth() === month) {
            if (tempDate.getDay() === dayOfWeek) {
                nth++;
                if (tempDate.getDate() === dateNum) break;
            }
            tempDate.setDate(tempDate.getDate() + 1);
        }

        return { dayOfWeek, nth };
    }

    function getNthWeekdayOfMonth(year, month, dayOfWeek, nth) {
        const d = new Date(year, month, 1);
        let count = 0;

        while (d.getMonth() === month) {
            if (d.getDay() === dayOfWeek) {
                count++;
                if (count === nth) return d;
            }
            d.setDate(d.getDate() + 1);
        }

        return null;
    }

    async function createMonthlyEvents(baseEvent, startLocal, endLocal) {
        const { dayOfWeek, nth } = findNthWeekday(startLocal);
        const startMonth = startLocal.getMonth();
        const startYear = startLocal.getFullYear();
        const durationMs = endLocal - startLocal;
        const promises = [];

        for (let i = 0; i < 12; i++) {
            const newMonth = startMonth + i;
            const newYear = startYear + Math.floor(newMonth / 12);
            const monthIndex = newMonth % 12;

            const newDate = getNthWeekdayOfMonth(newYear, monthIndex, dayOfWeek, nth);
            if (!newDate) continue;

            newDate.setHours(startLocal.getHours());
            newDate.setMinutes(startLocal.getMinutes());

            const newEnd = new Date(newDate.getTime() + durationMs);

            const monthlyEvent = {
                ...baseEvent,
                event_start: toLocalDateTimeString(newDate),
                event_end: toLocalDateTimeString(newEnd),
            };

            const p = fetch("/api/v1/events", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(monthlyEvent),
            }).then((res) => res.json());

            promises.push(p);
        }

        await Promise.all(promises);
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!user) {
            openLoginModal();
            return;
        }

        if (
            !selectedLodge ||
            !selectedConstitution ||
            !selectedEventType ||
            !startDateTime ||
            !endDateTime ||
            !description
        ) {
            return alert("Missing required fields.");
        }

        const startLocal = new Date(startDateTime);
        const endLocal = new Date(endDateTime);

        if (endLocal <= startLocal) {
            return alert("End date/time must be after start date/time.");
        }

        const baseEvent = {
            lodge_id: selectedLodge.value,
            constitution_id: selectedConstitution.value,
            event_type_id: selectedEventType.value,
            building_id: selectedBuilding?.value || null,
            description,
        };

        if (!isRecurring) {
            const singleEvt = {
                ...baseEvent,
                event_start: toLocalDateTimeString(startLocal),
                event_end: toLocalDateTimeString(endLocal),
            };

            try {
                const res = await fetch("/api/v1/events", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(singleEvt),
                });

                const data = await res.json();

                if (data.error) {
                    console.error(data);
                    alert(data.detail || data.error);
                    return;
                }

                reloadEvents();
                resetForm();
            } catch (err) {
                console.error("Error creating event:", err);
                alert("Error creating event.");
            }
        } else {
            try {
                await createMonthlyEvents(baseEvent, startLocal, endLocal);
                reloadEvents();
                resetForm();
            } catch (err) {
                console.error("Error creating recurring events:", err);
                alert("Error creating recurring events.");
            }
        }
    };

    function resetForm() {
        setSelectedLodge(null);
        setSelectedConstitution(null);
        setSelectedEventType(null);
        setSelectedBuilding(null);
        setStartDateTime("");
        setEndDateTime("");
        setDescription("");
        setIsRecurring(false);
    }

    const handleEventSelect = (evt) => {
        setEditingEvent(evt);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setEditingEvent(null);
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (editingEvent) {
            const lodgeOpt = lodges.find((l) => l.id === editingEvent.lodge_id);
            const constitutionOpt = constitutions.find((c) => c.id === editingEvent.constitution_id);
            const eventTypeOpt = eventTypes.find((et) => et.id === editingEvent.event_type_id);
            const buildingOpt = buildings.find((b) => b.id === editingEvent.building_id);

            setEditLodge(lodgeOpt ? { value: lodgeOpt.id, label: lodgeOpt.name } : null);
            setEditConstitution(
                constitutionOpt ? { value: constitutionOpt.id, label: constitutionOpt.description } : null
            );
            setEditEventType(
                eventTypeOpt ? { value: eventTypeOpt.id, label: eventTypeOpt.description } : null
            );
            setEditBuilding(buildingOpt ? { value: buildingOpt.id, label: buildingOpt.name } : null);

            const s = parseDbDate(editingEvent.event_start);
            const e = parseDbDate(editingEvent.event_end);

            setEditStartDateTime(format(s, "yyyy-MM-dd'T'HH:mm"));
            setEditEndDateTime(format(e, "yyyy-MM-dd'T'HH:mm"));
            setEditDescription(editingEvent.description);
        }
    }, [editingEvent, lodges, constitutions, eventTypes, buildings]);

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!editingEvent) return;

        if (!user) {
            openLoginModal();
            return;
        }

        if (
            !editLodge ||
            !editConstitution ||
            !editEventType ||
            !editStartDateTime ||
            !editEndDateTime ||
            !editDescription
        ) {
            return alert("Missing required fields.");
        }

        const sLocal = new Date(editStartDateTime);
        const eLocal = new Date(editEndDateTime);

        if (eLocal <= sLocal) {
            return alert("End date/time must be after start date/time.");
        }

        const updatedEvt = {
            lodge_id: editLodge.value,
            constitution_id: editConstitution.value,
            event_type_id: editEventType.value,
            building_id: editBuilding?.value || null,
            event_start: toLocalDateTimeString(sLocal),
            event_end: toLocalDateTimeString(eLocal),
            description: editDescription,
        };

        try {
            const res = await fetch(`/api/v1/events/${editingEvent.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatedEvt),
            });

            const data = await res.json();

            if (data.error) {
                console.error(data);
                alert(data.detail || data.error);
                return;
            }

            reloadEvents();
            closeModal();
        } catch (err) {
            console.error("Error updating event:", err);
            alert("Error updating event.");
        }
    };

    const handleDeleteEvent = async () => {
        if (!editingEvent) return;

        if (!user) {
            openLoginModal();
            return;
        }

        if (!window.confirm("Are you sure you want to delete this event?")) return;

        try {
            const res = await fetch(`/api/v1/events/${editingEvent.id}`, {
                method: "DELETE",
            });

            const data = await res.json();

            if (data.error) {
                console.error(data);
                alert(data.error);
                return;
            }

            reloadEvents();
            closeModal();
        } catch (err) {
            console.error("Error deleting event:", err);
            alert("Error deleting event.");
        }
    };

    const handleExportPdf = () => {
        const doc = new jsPDF();

        doc.setFontSize(16);
        doc.text("Calendar Agenda Export", 14, 16);

        const tableBody = filteredEvents.map((ev) => [
            format(ev.start, "dd/MM/yyyy"),
            format(ev.start, "HH:mm"),
            format(ev.end, "HH:mm"),
            ev.event_type || "",
            ev.lodge_name || "",
            ev.building_name ? `${ev.building_name}, ${ev.building_street || ""}` : "",
            ev.description || "",
        ]);

        doc.autoTable({
            startY: 25,
            head: [["Date", "Start", "End", "Type", "Lodge", "Building", "Description"]],
            body: tableBody,
            theme: "grid",
            headStyles: { fillColor: [100, 100, 100] },
            bodyStyles: { fontSize: 10 },
        });

        doc.save("calendar_agenda.pdf");
    };

    const handleExportCsv = () => {
        const rows = [
            ["Date", "Start", "End", "Event Type", "Lodge", "Venue", "Description"],
            ...filteredEvents
                .slice()
                .sort((a, b) => a.start - b.start)
                .map((ev) => [
                    ev.start ? format(ev.start, "yyyy-MM-dd") : "",
                    ev.start ? format(ev.start, "HH:mm") : "",
                    ev.end ? format(ev.end, "HH:mm") : "",
                    ev.event_type || "",
                    ev.lodge_name || "",
                    ev.building_name || "",
                    ev.description || "",
                ]),
        ];

        const csv = rows
            .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "calendar_events.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const formats = {
        dayFormat: (date, culture, localizer) =>
            localizer.format(date, "dd/MM/yyyy", culture),
        agendaDateFormat: (date, culture, localizer) =>
            localizer.format(date, "dd/MM/yyyy", culture),
        agendaTimeFormat: (date, culture, localizer) =>
            localizer.format(date, "HH:mm", culture),
    };

    const eventPropGetter = (ev) => {
        const style = {
            backgroundColor: "#6c757d",
            borderRadius: "5px",
            border: "none",
            color: "#fff",
            padding: "2px 5px",
            fontSize: "0.85rem",
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: 1.2,
        };

        const lower = (ev.event_type || "").toLowerCase();

        if (lower.includes("regular")) style.backgroundColor = "#76c8dd";
        else if (lower.includes("installation")) style.backgroundColor = "#28a745";
        else if (lower.includes("royal arch")) style.backgroundColor = "#e83e8c";
        else if (lower.includes("knight masons")) style.backgroundColor = "#17a2b8";
        else if (lower.includes("knights templar")) style.backgroundColor = "#6610f2";
        else if (lower.includes("stated communication")) style.backgroundColor = "#fd7e14";
        else if (lower.includes("management")) style.backgroundColor = "#7a7a7a";
        else if (lower.includes("fundraising")) style.backgroundColor = "#ffc107";

        return { style };
    };

    return (
        <main className="calendar-page">
            <section className="calendar-hero">
                <div>
                    <p className="calendar-eyebrow">Lodge programme</p>
                    <h1>Calendar & Events</h1>
                    <p>
                        Plan meetings, installations, communications, fundraising activity, and research events from one
                        shared programme. Filter the calendar, export the agenda, and keep useful Masonic links close at hand.
                    </p>
                </div>
                <div className="calendar-next-card">
                    <span>Next event</span>
                    {nextEvent ? (
                        <>
                            <strong>{format(nextEvent.start, "dd MMM yyyy")}</strong>
                            <p>{nextEvent.lodge_name || "Unknown Lodge"}</p>
                            <small>{nextEvent.event_type || "Event"} {nextEvent.start ? format(nextEvent.start, "HH:mm") : ""}</small>
                        </>
                    ) : (
                        <>
                            <strong>No upcoming events</strong>
                            <p>Add a new event below to start building the programme.</p>
                        </>
                    )}
                </div>
            </section>

            <section className="calendar-stat-grid" aria-label="Calendar summary">
                <div className="calendar-stat-card">
                    <span>Visible events</span>
                    <strong>{filteredEvents.length}</strong>
                    <small>{activeFilterCount ? `${activeFilterCount} filters active` : "Showing all current events"}</small>
                </div>
                <div className="calendar-stat-card">
                    <span>Upcoming</span>
                    <strong>{upcomingEvents.length}</strong>
                    <small>Next events in the filtered view</small>
                </div>
                <div className="calendar-stat-card">
                    <span>Lodges</span>
                    <strong>{uniqueLodgeCount}</strong>
                    <small>Represented in this view</small>
                </div>
                <div className="calendar-stat-card">
                    <span>Venues</span>
                    <strong>{uniqueVenueCount}</strong>
                    <small>Linked building records</small>
                </div>
            </section>

            <section className="calendar-layout">
                <aside className="calendar-side">
                    <div className="calendar-panel">
                        <div className="calendar-panel-heading">
                            <p>Filters</p>
                            <h2>Refine Programme</h2>
                        </div>
                        <div className="calendar-field">
                            <label>Filter by Lodge(s)</label>
                            <Select
                                options={lodgeOptions}
                                value={selectedLodgeFilters}
                                onChange={(opts) => setSelectedLodgeFilters(opts || [])}
                                placeholder="All Lodges"
                                isMulti
                                isClearable
                            />
                        </div>

                        <div className="calendar-field">
                            <label>Filter by Event Type(s)</label>
                            <Select
                                options={eventTypeFilterOptions}
                                value={selectedEventTypeFilters}
                                onChange={(opts) => setSelectedEventTypeFilters(opts || [])}
                                placeholder="All Event Types"
                                isMulti
                                isClearable
                            />
                        </div>

                        <div className="calendar-actions">
                            <button type="button" onClick={handleExportPdf}>Export PDF</button>
                            <button type="button" onClick={handleExportCsv}>Export CSV</button>
                            <button
                                type="button"
                                className="calendar-secondary"
                                onClick={() => {
                                    setSelectedLodgeFilters([]);
                                    setSelectedEventTypeFilters([]);
                                }}
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>

                    <div className="calendar-panel">
                        <div className="calendar-panel-heading">
                            <p>Upcoming</p>
                            <h2>Next Dates</h2>
                        </div>
                        <div className="calendar-upcoming-list">
                            {upcomingEvents.length === 0 && <p className="calendar-empty">No upcoming events match these filters.</p>}
                            {upcomingEvents.map((event) => (
                                <button type="button" key={event.id} onClick={() => handleEventSelect(event)}>
                                    <span>{format(event.start, "dd MMM")}</span>
                                    <strong>{event.lodge_name || "Unknown Lodge"}</strong>
                                    <small>{event.event_type || "Event"} {format(event.start, "HH:mm")}</small>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="calendar-panel">
                        <div className="calendar-panel-heading">
                            <p>Event mix</p>
                            <h2>Top Types</h2>
                        </div>
                        <div className="calendar-type-list">
                            {eventTypeCounts.map((item) => (
                                <div key={item.label}>
                                    <span>{item.label}</span>
                                    <strong>{item.count}</strong>
                                </div>
                            ))}
                            {eventTypeCounts.length === 0 && <p className="calendar-empty">No event types yet.</p>}
                        </div>
                    </div>
                </aside>

                <div className="calendar-main">
                    <div className="big-calendar-container">
                        <div className="calendar-panel-heading split">
                            <div>
                                <p>Calendar</p>
                                <h2>Programme View</h2>
                            </div>
                            <span>{filteredEvents.length} events</span>
                        </div>
                <Calendar
                    localizer={localizer}
                    events={filteredEvents}
                    startAccessor="start"
                    endAccessor="end"
                    titleAccessor="title"
                    tooltipAccessor="tooltip"
                    views={["month", "week", "day", "agenda"]}
                    defaultView={window.matchMedia("(max-width: 768px)").matches ? "agenda" : "month"}
                    length={365}
                            style={{ height: 660 }}
                    eventPropGetter={eventPropGetter}
                    formats={formats}
                    onSelectEvent={handleEventSelect}
                    selectable={false}
                />
                    </div>
                </div>
            </section>

            <section className="calendar-bottom-grid single">
                <section className="add-event-form">
                    <div className="calendar-panel-heading">
                        <p>New date</p>
                        <h2>Add a New Event</h2>
                    </div>

                <form onSubmit={handleSubmit}>
                        <div className="calendar-form-grid">
                    <div className="form-group">
                        <label>Lodge</label>
                        <Select
                            options={lodges.map((l) => ({ label: l.name, value: l.id }))}
                            value={selectedLodge}
                            onChange={setSelectedLodge}
                            placeholder="Select Lodge"
                            isClearable
                        />
                    </div>

                    <div className="form-group">
                        <label>Constitution</label>
                        <Select
                            options={constitutions.map((c) => ({ label: c.description, value: c.id }))}
                            value={selectedConstitution}
                            onChange={setSelectedConstitution}
                            placeholder="Select Constitution"
                            isClearable
                        />
                    </div>

                    <div className="form-group">
                        <label>Event Type</label>
                        <Select
                            options={eventTypes.map((et) => ({ label: et.description, value: et.id }))}
                            value={selectedEventType}
                            onChange={setSelectedEventType}
                            placeholder="Select Event Type"
                            isClearable
                        />
                    </div>

                    <div className="form-group">
                        <label>Building / Address</label>
                        <Select
                            options={buildingOptions}
                            value={selectedBuilding}
                            onChange={setSelectedBuilding}
                            placeholder="Select Building"
                            isClearable
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="start-datetime">Start Date & Time</label>
                        <input
                            type="datetime-local"
                            id="start-datetime"
                            value={startDateTime}
                            onChange={(e) => setStartDateTime(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="end-datetime">End Date & Time</label>
                        <input
                            type="datetime-local"
                            id="end-datetime"
                            value={endDateTime}
                            onChange={(e) => setEndDateTime(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group form-group-wide">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="3"
                            required
                        />
                    </div>
                        </div>

                    <div className="calendar-repeat-row">
                        <label>
                            <input
                                type="checkbox"
                                checked={isRecurring}
                                onChange={(e) => setIsRecurring(e.target.checked)}
                                style={{ marginRight: "0.5rem" }}
                            />
                            Repeat Monthly (on same nth weekday)?
                        </label>
                            <span>{recurringPreviewText}</span>
                    </div>

                        <button className="calendar-primary-btn" type="submit">Add Event</button>
                </form>
                </section>
            </section>

            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="Edit Event Modal"
                className="modal-content"
                overlayClassName="modal-overlay"
            >
                <h2>Edit / Delete Event</h2>

                {editingEvent && (
                    <form onSubmit={handleEditSubmit}>
                        <div className="form-group">
                            <label>Lodge:</label>
                            <Select
                                options={lodges.map((l) => ({ label: l.name, value: l.id }))}
                                value={editLodge}
                                onChange={setEditLodge}
                                placeholder="Select Lodge"
                                isClearable
                            />
                        </div>

                        <div className="form-group">
                            <label>Constitution:</label>
                            <Select
                                options={constitutions.map((c) => ({ label: c.description, value: c.id }))}
                                value={editConstitution}
                                onChange={setEditConstitution}
                                placeholder="Select Constitution"
                                isClearable
                            />
                        </div>

                        <div className="form-group">
                            <label>Event Type:</label>
                            <Select
                                options={eventTypes.map((et) => ({ label: et.description, value: et.id }))}
                                value={editEventType}
                                onChange={setEditEventType}
                                placeholder="Select Event Type"
                                isClearable
                            />
                        </div>

                        <div className="form-group">
                            <label>Building (Address):</label>
                            <Select
                                options={buildingOptions}
                                value={editBuilding}
                                onChange={setEditBuilding}
                                placeholder="Select Building"
                                isClearable
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-start-datetime">Start Date & Time:</label>
                            <input
                                type="datetime-local"
                                id="edit-start-datetime"
                                value={editStartDateTime}
                                onChange={(e) => setEditStartDateTime(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-end-datetime">End Date & Time:</label>
                            <input
                                type="datetime-local"
                                id="edit-end-datetime"
                                value={editEndDateTime}
                                onChange={(e) => setEditEndDateTime(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-description">Description:</label>
                            <textarea
                                id="edit-description"
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                rows="3"
                                required
                            />
                        </div>

                        <button type="submit">Save Changes</button>

                        <button
                            type="button"
                            onClick={handleDeleteEvent}
                            className="delete-button"
                        >
                            Delete Event
                        </button>

                        <button type="button" onClick={closeModal}>
                            Cancel
                        </button>
                    </form>
                )}
            </Modal>
        </main>
    );
}

export default CalendarPage;
